import i18n from './modules/i18n'
import query from './modules/query'
import cubeMeta from './modules/cubeMeta'
import data from './modules/data'
import tags from './modules/tags'
import external from './modules/external'
import xhrError from './modules/xhrError'

import Vue from 'vue'
import Vuex from 'vuex'

import { mergeBreakdowns, mergeTimes } from '@/utils/query'
import { filterChartRowByTimeScale } from '@/utils/dates'
import {
  getBreakdownDimensions,
  extendCubeWithConfig,
  extendDataWithMeta,
  extendDataWithConfig,
} from '@/utils/cube'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {},
  mutations: {},
  actions: {
    checkIfPreloaded(context) {
      // Muy retorcido. Casi tanto como el modelo de datos del proyecto...  \_(·-·)_/
      if (context.state.external.versionInfo && context.state.i18n.lng) return Promise.resolve()
      return new Promise(res => {
        const unsubscribe = store.watch(
          function observer(state) {
            return state.external.versionInfo && state.i18n.lng && state.tags.tree
          },
          function watcher(to) {
            if (to) {
              unsubscribe()
              res()
            }
          }
        )
      })
    },
  },
  modules: {
    i18n,
    tags,
    query,
    cubeMeta,
    data,
    external,
    xhrError,
  },
})

function getMode() {
  const match = location.hash.match(/\/(explore|compare)\//)
  if (match) return match[1]
  else 'explore'
}

// Watch to cubeId changes to perform cubeMeta requests
function watchCube(slot) {
  store.watch(
    function observer(state) {
      return state.query[slot + 'CubeId']
    },
    function watcher(to) {
      if ((slot === 'pair' || slot === 'size') && getMode() !== 'compare') return
      store.commit(slot + 'CubeMeta', null)
      store.commit(slot + 'Data', null)
      if (!to) return

      store
        .dispatch('checkIfPreloaded')
        .then(() => {
          return store
            .dispatch('getCubeMeta', {
              cubeId: to,
              sourceSlots:
                getMode() !== 'compare'
                  ? 1
                  : [
                    store.state.query.mainCubeId,
                    store.state.query.pairCubeId,
                    store.state.query.sizeCubeId,
                  ].filter(d => d).length,
            })
            .then(meta => {
              store.commit(slot + 'CubeMeta', meta)
              commitBreakdowns(slot, meta)
            })
        })
        .catch(err => {
          console.error(err)
        })
    },
    {
      immediate: true,
    }
  )
}

function watchQuery(slot) {
  let _lastQuery
  store.watch(
    function observer(state) {
      return {
        meta: state.cubeMeta[slot] != null,
        cubeId: state.query[slot + 'CubeId'],
        indId: state.query[slot + 'IndId'],
        breakdowns: state.query[slot + 'Breakdowns'],
        // lollipop: state.query.lollipop,
        lollipopOpts: state.query.lollipopOpts,
        flip: state.query.flip,
      }
    },
    function watcher(to, from) {
      const isFirstObservation = _lastQuery === void 0
      if (
        !(to.meta && to.cubeId && to.indId && Object.keys(to.breakdowns).length) ||
        ((slot === 'pair' || slot === 'size') && getMode() !== 'compare')
      ) {
        _lastQuery = null
        return
      }

      const changes = []
      let hasChanged = Object.keys(to).reduce((hasChanged, k) => {
        let change = false
        switch (k) {
          case 'breakdowns':
            change =
              Object.keys(to.breakdowns).filter(k => from.breakdowns[k]).length !==
              Object.keys(to.breakdowns).length ||
              Object.keys(to.breakdowns).reduce((hasChanged, dimId) => {
                return hasChanged || to.breakdowns[dimId] !== from.breakdowns[dimId]
              }, false)
            break
          case 'lollipopOpts':
            change =
              to.lollipopOpts.reduce((hasChanged, iso) => {
                return hasChanged || !from.lollipopOpts.includes(iso)
              }, false) ||
              from.lollipopOpts.reduce((hasChanged, iso) => {
                return hasChanged || !to.lollipopOpts.includes(iso)
              }, false)
            break
          default:
            change = to[k] != from[k]
        }
        if (change) changes.push(k)
        return change || hasChanged
      }, false)

      if (hasChanged === false) return

      // toBeFlipped can be because a flip change or if flip is active at the start
      const toBeFlipped =
        changes.filter(d => d === 'flip').length > 0 || (isFirstObservation && to.flip != false)
      const isNDSC = /DF_NDSC$/i.test(to.cubeId)
      const parsedBreakdowns = store.state.cubeMeta[slot + 'BreakdownDimensions']
      const dataQueryParams = store.getters.dataQuery(
        slot,
        store.getters[slot + 'Dimensions'].map(dim => {
          const candidate = parsedBreakdowns.find(d => d.id == dim.id)
          if (candidate) return candidate
          return dim
        }),
        store.getters[slot + 'TimeDimension'],
        {
          hasLollipop: getMode() !== 'compare',
          isNDSC: isNDSC,
        }
      )
      dataQueryParams.cubeVersion = store.getters[slot + 'CubeVersion']

      // check if the query is updated
      hasChanged = (hasChanged && JSON.stringify(dataQueryParams) !== _lastQuery) || toBeFlipped

      // If we reach this point because is NDSC cube or because a dimension flip but
      // we shouldn't request more data, exit
      if (!hasChanged && isNDSC) return

      const config = store.state.external.versionInfo.cubes.find(cube => cube.metaId === to.cubeId)
      const meta = store.state.cubeMeta[slot]

      // if has to be flipped, first update state and delay the data fetch to the next state change observation
      if (toBeFlipped) {
        const flip = store.state.query.flip

        // reset pair and size cube selection if it isn't equal to the main cube id
        if (store.state.query.mainCubeId != store.state.query.pairCubeId) {
          store.state.query.pairCubeId !== null && store.commit('pairCubeId', null)
        }
        if (store.state.query.mainCubeId != store.state.query.sizeCubeId) {
          store.state.query.sizeCubeId !== null && store.commit('sizeCubeId', null)
        }

        // Si no se pone al final de la pila los componentes de vue congelados. WTF!
        commitBreakdowns(slot, extendCubeWithConfig(meta, { config, flip }))

        // If other cubes selections persists, then they are the same cube, perform a shallow copy and store
        // the same results for them
        if (slot !== 'pair' && store.state.query.pairCubeId) {
          store.commit('pairCubeMeta', null)
          setTimeout(() => {
            store.commit('pairCubeMeta', meta)
            commitBreakdowns('pair', extendCubeWithConfig(meta, { config, flip }))
          }, 0)
        }

        if (slot !== 'size' && store.state.query.sizeCubeId) {
          this.$store.commit('sizeCubeMeta', null)
          setTimeout(() => {
            this.$store.commit('sizeCubeMeta', meta)
            commitBreakdowns('size', extendCubeWithConfig(meta, { config, flip }))
          }, 0)
        }

        if (isNDSC && store.state.data[slot]) {
          // In case is NDSC, then we have to reconfigure data dimension types
          const data = extendDataWithMeta(
            extendDataWithConfig(store.state.data[slot], {
              meta,
              config,
              flip: store.state.query.flip,
            }),
            meta
          )
          store.commit(slot + 'Data', null)
          setTimeout(() => store.commit(slot + 'Data', data), 1000)
        }

        store.commit('lollipop', null)

        // If is flipped, abort data fetching and delegate to the immediate observable change except when is firstObservation
        if (!isFirstObservation) return
      }

      store.commit(slot + 'Data', null)
      store.commit(slot + 'Constraints', null)

      const constraintsReq = store
        .dispatch('getDataConstraints', {
          slot,
          cubeId: dataQueryParams.cubeId,
          cubeVersion: dataQueryParams.cubeVersion,
          dimensions: dataQueryParams.dimensions
            .map((d, i) => (store.getters[slot + 'Dimensions'][i].type === 'indicator' ? d : []))
            .map((v, i) => (store.getters[slot + 'Dimensions'][i].omissible ? [] : v)),
          fromYear: 2005, // store.getters.queryTime[0].getFullYear(),
          cubeSource: config.cubeSource,
        })
        .then(constraints => store.commit(slot + 'Constraints', constraints))

      // defer data request until constraints is resolved on constrained cubes
      const constraintsLock =
        config.constrained === true
          ? constraintsReq.then(() => {
            const constraints = store.getters[slot + 'Constraints']
            constraints.forEach(dimConstraints => {
              if (!Array.isArray(dimConstraints.values)) return

              const constraintValues = Object.fromEntries(
                dimConstraints.values.map(v => [v, true])
              )
              const dim = store.state.cubeMeta[
                slot
              ].dataStructures[0].dataStructureComponents.dimensionList.dimensions.find(
                d => d.id === dimConstraints.id
              )
              const codelist = store.state.cubeMeta[slot].codelists.find(
                cl => cl.id === dim.codelist
              )
              dimConstraints.values = codelist.codes.reduce((values, code) => {
                if (constraintValues[code.id]) {
                  values.push(code.id)
                }
                return values
              }, [])
            })
            const queryMutations = store.getters.queryMutations(constraints, slot)
            if (Object.keys(queryMutations).length) {
              store.commit(slot + 'Breakdowns', {
                ...store.state.query[slot + 'Breakdowns'],
                ...queryMutations,
              })
              return true
            }
          })
          : Promise.resolve(false)

      constraintsLock.then(abort => {
        // abort data request on query mutations based on constraints
        if (abort) return
        store
          .dispatch('getData', {
            slot,
            meta,
            config,
            flip: store.state.query.flip,
            sourceSlots:
              getMode() !== 'compare'
                ? 1
                : [
                  store.state.query.mainIndId,
                  store.state.query.pairIndId,
                  store.state.query.sizeIndId,
                ].filter(d => d).length,
            cubeId: dataQueryParams.cubeId,
            cubeVersion: dataQueryParams.cubeVersion,
            dimensions: dataQueryParams.dimensions.map((v, i) => {
              return store.getters[slot + 'Dimensions'][i].omissible ? [] : v
            }),
          })
          .then(data => {
            store.commit(slot + 'Data', data)

            if (slot === 'main') {
              const initialTimeScale = store.state.query.timeScale
              let timeScale = store.getters.mainTimeScales.find(d => d.id === initialTimeScale)?.id

              if (timeScale === void 0 || changes.includes('meta')) {
                const defaultTimeScale = store.getters.mainTimeScaleDimension?.options.find(
                  opt => opt.isDefault
                )?.id
                timeScale =
                  store.getters.mainTimeScales.find(d => d.id === defaultTimeScale)?.id ||
                  store.getters.mainTimeScales[0]?.id ||
                  'A'
                store.commit('timeScale', timeScale)
              }

              const utRange = store.state.query.utRange
              const dataDates = store.getters.mainAvailableDates.filter(date =>
                filterChartRowByTimeScale(date, timeScale)
              )
              if (dataDates.length > 0) {
                if (
                  to.cubeId !== from.cubeId
                  || timeScale !== initialTimeScale
                  || utRange !== true
                ) {
                  store.commit('time', [
                    dataDates[0].getTime(),
                    dataDates[dataDates.length - 1].getTime(),
                  ])
                } else {
                  store.commit(
                    'time',
                    mergeTimes(
                      store.getters.queryTime.map(d => d.getTime()),
                      [dataDates[0].getTime(), dataDates[dataDates.length - 1].getTime()]
                    )
                  )
                }
              } else {
                store.commit('time', [])
              }
            }

            const units = store.getters[slot + 'Units']
            const initialUnit = store.state.query[slot + 'Unit']
            let unit = units.find(d => d.id === initialUnit)?.id

            if (unit === void 0) {
              const defaultUnit = store.getters[slot + 'UnitDimension']?.options.find(
                opt => opt.isDefault
              )?.id
              unit = units.find(d => d.id === defaultUnit)?.id || units[0]?.id || ''
              store.commit(slot + 'Unit', unit)
            }
          })
      })

      _lastQuery = JSON.stringify(dataQueryParams)
    },
    {
      deep: true,
    }
  )
}

function commitBreakdowns(slot, meta) {
  const breakdowns = getBreakdownDimensions(meta)
  store.commit(slot + 'BreakdownDimensions', breakdowns)

  if (store.state.query.flip) {
    if (slot !== 'main') {
      // Si estamos en modo flip y es la primera vez que seleccionamos un cubo en un slot distinto del main
      // reaprovechamos la selección de breakdowns del main para hacer mas predecible al usuario la selección
      if (Object.keys(store.state.query[slot + 'Breakdowns']).length === 0) {
        store.commit(slot + 'Breakdowns', store.state.query.mainBreakdowns)
        return
      }
    }
  }

  store.commit(
    slot + 'Breakdowns',
    mergeBreakdowns(
      store.state.query[slot + 'Breakdowns'],
      store.state.cubeMeta[slot + 'BreakdownDimensions'] || []
    )
  )
}

const slots = ['main', 'pair', 'size']
for (let slot of slots) {
  watchCube(slot)
  watchQuery(slot)
}

export default store
